import {
    organizationGroupErrorMessage,
    useCreateOrganizationGroup,
} from "@/api/organization-groups";
import {
    OrganizationCombobox,
    type EntityComboboxValidity,
} from "@/components/shared/OrganizationCombobox";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useId, useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { toast } from "sonner";

export function CreateGroupDialog() {
    const ownerLabelId = useId();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [ownerId, setOwnerId] = useState<number | null>(null);
    const [ownerValidity, setOwnerValidity] =
        useState<EntityComboboxValidity>("empty");
    const [conflict, setConflict] = useState<string | null>(null);
    const [showValidation, setShowValidation] = useState(false);
    const createGroup = useCreateOrganizationGroup();
    const submission = useSingleFlight();

    const canSubmit =
        name.trim().length > 0 && ownerId !== null && ownerValidity === "valid";

    const reset = () => {
        setName("");
        setOwnerId(null);
        setOwnerValidity("empty");
        setConflict(null);
        setShowValidation(false);
    };

    const handleOpenChange = (next: boolean) => {
        if (!next && submission.isRunning()) return;
        setOpen(next);
        if (!next) reset();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) {
            setShowValidation(true);
            return;
        }
        setConflict(null);
        await submission.run(async () => {
            try {
                await createGroup.mutateAsync({
                    name,
                    owner_organization_id: ownerId,
                });
                setOpen(false);
                reset();
            } catch (error) {
                const message = organizationGroupErrorMessage(
                    error,
                    "Couldn't create the group. Please try again."
                );
                setConflict(message);
                toast.error(message);
            }
        });
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger render={<Button>Create group</Button>} />
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                <form onSubmit={handleSubmit} noValidate>
                    <DialogHeader>
                        <DialogTitle>Create group</DialogTitle>
                        <DialogDescription>
                            The owner organization keeps the shared knowledge
                            base. Members read from it.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="organization-group-name">
                                Group name
                            </Label>
                            <Input
                                id="organization-group-name"
                                maxLength={120}
                                value={name}
                                onChange={(event) =>
                                    setName(event.target.value)
                                }
                                autoComplete="off"
                                disabled={createGroup.isPending}
                            />
                        </div>
                        <div className="grid gap-2">
                            <span
                                id={ownerLabelId}
                                className="text-foreground block text-sm font-medium"
                            >
                                Owner
                            </span>
                            <div role="group" aria-labelledby={ownerLabelId}>
                                <OrganizationCombobox
                                    mode="entity"
                                    required
                                    name="owner_organization_id"
                                    value={ownerId}
                                    onChange={setOwnerId}
                                    showValidation={showValidation}
                                    onValidityChange={setOwnerValidity}
                                    disabled={createGroup.isPending}
                                />
                            </div>
                        </div>
                        {conflict && (
                            <p
                                role="alert"
                                className="text-destructive text-sm"
                            >
                                {conflict}
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            disabled={createGroup.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            aria-disabled={!canSubmit || createGroup.isPending}
                            disabled={createGroup.isPending}
                        >
                            Create group
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
